import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import {
  AlertTriangle, AlertCircle, Info, RefreshCw, RefreshCwOff, Navigation, Calendar, UserCheck, Clock, Compass, DollarSign,
  Truck as TruckIcon, ChevronDown, ChevronUp, Check, ArrowRight, Fuel,
} from "lucide-react";
import {
  BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer,
} from "recharts";
import { apiBaseUrl, authHeaders, formatHm } from "./shared";
import { useAlerts } from "../../hooks/useAlerts";
import LoadingState from "./LoadingState";
import RouteMonitorModal from "../../components/RouteMonitorModal";
import { buildHistoryQuery, formatDuration, isRouteType, keyFact, typeLabel, windowLabel } from "./alertsHistory";

// Each type's `stat` renders the one number that actually matters for that
// alert, colored by severity, so a truck's alert list reads as a row of
// facts ("Off route +230mi", "Toll overage +$43") instead of a wall of
// prose -- the full original `message` is still there, just moved to the
// row's title tooltip for whoever wants the complete sentence.
const TYPE_META = {
  eta_drift: {
    icon: Navigation, label: "ETA drift", tab: "route",
    stat: (a) => `${formatHm(Math.abs(a.drift_hours))} ${a.drift_hours > 0 ? "late" : "early"}`,
  },
  pto_conflict: {
    icon: Calendar, label: "PTO conflict", tab: "overview",
    stat: (a) => (a.time_off_start ? `starts ${a.time_off_start.slice(0, 10)}` : "time off overlaps"),
  },
  returning_driver_available: {
    icon: UserCheck, label: "Driver available", tab: "overview",
    stat: () => "ready to dispatch",
  },
  // Purely informational -- see hos_violations table (backend/motive_sync.py
  // _check_hos_violations, run every 1 minute alongside the Motive sync).
  // Opens the moment a driver is caught driving with 0h daily/cycle HOS
  // left, closes the moment they stop -- the closed ones stay in that table
  // as the real historical record even after they drop off this list.
  hos_violation: {
    icon: Clock, label: "HOS violation", tab: "overview",
    // Real magnitude (actual driving hours vs the FMCSA limit), not time
    // since detection -- see backend/motive_sync.py's _check_hos_violations.
    stat: (a) => (a.hours_over != null ? `+${formatHm(a.hours_over)} over` : "over limit"),
  },
  // Backed by truck_route_breadcrumbs (motive_sync.py, ~every 1 minute) --
  // opens on the Route tab, not overview, since that's where the "Monitor"
  // button lives to actually see the planned-vs-actual path.
  off_route: {
    icon: Compass, label: "Off route", tab: "route",
    stat: (a) => `${a.distance_from_route_miles}mi off`,
  },
  // Real toll spend (truck_toll_passes, priced against every route HERE
  // quoted for this load, not just the chosen one -- see motive_sync.py's
  // _record_toll_pass) already exceeding the chosen route's own planned
  // total. Opens the Route tab, same as off_route -- that's where a
  // dispatcher would go to see which path was actually driven.
  toll_overage: {
    icon: DollarSign, label: "Toll overage", tab: "route",
    stat: (a) => `+$${Number(a.toll_overage).toFixed(2)}`,
  },
  // Real unplanned truck_fuel_fills for this load (motive_sync.py's
  // _check_fuel_deviation_events, reusing fleet_stats.py's
  // fuel_stop_reconciliation) -- a fill that matched no planned stop
  // within fleet.fuel_stop_match_miles. Opens the Route tab, same as
  // off_route/toll_overage, since that's where the fuel-stop pins live.
  fuel_deviation: {
    icon: Fuel, label: "Fuel deviation", tab: "route",
    stat: (a) => (a.unplanned_stop_count != null ? `${a.unplanned_stop_count} unplanned` : "unplanned stop"),
  },
  // Fleet-wide, not per-truck (see backend/motive_sync.py's
  // motive.last_sync_completed_at write + /fleet/alerts) -- has no
  // truck_id, rendered as its own banner above the per-truck list instead
  // of forced into a truck card it doesn't belong to.
  motive_sync_stale: {
    icon: RefreshCwOff, label: "Motive sync stale",
    stat: (a) => `${Math.round(a.minutes_since_sync)}m stale`,
  },
  // Stop-detection CANDIDATES (backend/stop_detection.py + motive_sync.py):
  // the truck dwelled at the facility then left, but no pickup/delivery was
  // ever confirmed. ALERT-ONLY during the observation rollout -- a dispatcher
  // Confirms (routes through the canonical lifecycle path) or Dismisses; the
  // engine never mutates the load itself. stat shows the deterministic
  // evidence level + how far the truck is now.
  possible_missed_pickup: {
    icon: TruckIcon, label: "Possible missed pickup", tab: "overview",
    stat: (a) => `${a.confidence || "?"}${a.current_distance_miles != null ? ` · ${Math.round(a.current_distance_miles)}mi away` : ""}`,
  },
  possible_missed_delivery: {
    icon: TruckIcon, label: "Possible missed delivery", tab: "overview",
    stat: (a) => `${a.confidence || "?"}${a.current_distance_miles != null ? ` · ${Math.round(a.current_distance_miles)}mi away` : ""}`,
  },
};

const MISSED_STOP_TYPES = new Set(["possible_missed_pickup", "possible_missed_delivery"]);

const SEVERITY_META = {
  critical: { color: "#dc2626", bg: "rgba(220,38,38,0.08)", icon: AlertCircle, label: "Critical" },
  warning: { color: "#a16207", bg: "rgba(234,179,8,0.1)", icon: AlertTriangle, label: "Warning" },
  info: { color: "#2563eb", bg: "rgba(37,99,235,0.08)", icon: Info, label: "Info" },
};
// Lower = worse -- drives "which truck/dispatcher is the hottest" sorting
// throughout this page, same convention the backend's own alerts.sort uses.
const SEVERITY_WEIGHT = { critical: 0, warning: 1, info: 2 };

const TYPE_CHART_COLORS = ["#1d6fd8", "#5b9bd5", "#a78bfa", "#f2a33d", "#e0745d", "#4cae6d"];

// Computed on demand from real data (route_options.live_eta vs the
// committed trip_plan.eta, driver_time_off overlapping an in-transit load's
// delivery window, drivers.last_truck_id fallback with no live Motive
// current_driver_id, truck_route_breadcrumbs for off-route) -- see
// backend/fleet_alerts scripted API. Nothing here is stored server-side;
// every load is a fresh recompute, so this always reflects the current
// state of the fleet, not a stale snapshot.
//
// Scoping is done client-side (not a backend param) since it's a pure
// ownership filter on data the page already has to fetch in full for the
// fleet-pattern charts to make sense at all -- a dispatcher's own view is
// just the admin view with a truck-ownership filter applied on top, not a
// materially different query.
//
// The page tells two different stories to two different audiences: a
// dispatcher wants "which of MY trucks needs help, and why" (truck-first);
// an admin additionally wants "where is the heat concentrated across the
// fleet" (dispatcher-then-truck, plus the fleet-pattern charts). Grouping
// by alert TYPE first (the previous design) answered neither question well
// -- a truck with three simultaneous problems got split across three
// unrelated sections, and nothing on the page said "here's the truck that
// actually needs you right now."
export default function Alerts() {
  // Auto-refreshing (60s) via the shared hook -- same source the sidebar
  // bell/DispatcherBoard bell/dashboards all now read from, so counts can
  // never disagree across pages.
  const { data, loading: alertsLoading, error: alertsError, reload: reloadAlerts } = useAlerts(apiBaseUrl, authHeaders);
  const [trucks, setTrucks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isLoading = loading || alertsLoading;
  const combinedError = error || alertsError;
  // Which dispatcher sections are expanded (admin view only) -- collapsed
  // by default, one compact row per dispatcher, so 10 dispatchers fit on
  // one screen instead of every one of their trucks rendering fully
  // expanded at once. Auto-expands the single worst dispatcher once real
  // data lands (see the effect below); every other toggle after that is
  // the admin's own click, never overwritten by a later refresh.
  const [expandedDispatchers, setExpandedDispatchers] = useState(() => new Set());
  // "Truck" (default) answers "which truck needs me right now" -- the
  // deliberate design already in place below. "Type" re-slices the same
  // alerts for a targeted review ("show me every toll overage this week"),
  // which the truck-first view can't answer without opening every card.
  // Same toggle shape as Truck Economics' Truck/Dispatcher switch.
  const [groupBy, setGroupBy] = useState("truck");
  const autoExpandedRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  // Control Tower's Dispatcher Overview links here with the clicked
  // dispatcher's id in router state, so the admin lands directly on that
  // dispatcher's group instead of the worst-first default below.
  const targetDispatcherId = location.state?.targetDispatcherId || null;
  const targetGroupRef = useRef(null);
  // Set only for toll_overage/off_route/eta_drift ("route"-tab alert
  // types) -- clicking one of those opens the planned-vs-actual map
  // directly (with the alert's own numbers in the context strip) instead
  // of navigating away to the Operational Center's Route tab.
  const [routeModal, setRouteModal] = useState(null);

  // --- Incident history (Phase 1): OPEN uses the existing live feed above;
  // RESOLVED/ALL query the read-only /fleet/alert-history endpoint. ---
  const [statusTab, setStatusTab] = useState("open");
  const [histFilters, setHistFilters] = useState({ severity: "", type: "", dispatcher: "", q: "", from: "", to: "" });
  const [history, setHistory] = useState({ incidents: [], page: null, counts: null, window: null, loading: false, error: null });
  const [detailIncident, setDetailIncident] = useState(null);
  const historyReqRef = useRef(0);

  function loadHistory(reset) {
    if (statusTab === "open") return;
    const cursor = reset ? null : history.page?.next_cursor;
    if (!reset && !cursor) return;
    const reqId = ++historyReqRef.current;
    setHistory((h) => ({ ...h, loading: true, error: null }));
    axios
      .get(`${apiBaseUrl}/fleet/alert-history?${buildHistoryQuery(statusTab, histFilters, cursor)}`, authHeaders())
      .then((res) => {
        if (reqId !== historyReqRef.current) return; // ignore out-of-order responses
        const d = res.data || {};
        setHistory((h) => ({
          incidents: reset ? d.incidents || [] : [...h.incidents, ...(d.incidents || [])],
          page: d.page || null,
          counts: d.counts != null ? d.counts : h.counts, // counts only returned on first page
          window: d.window || h.window,
          loading: false,
          error: null,
        }));
      })
      .catch((err) => {
        if (reqId !== historyReqRef.current) return;
        setHistory((h) => ({ ...h, loading: false, error: err.response?.data?.detail || err.message }));
      });
  }

  // Re-query from the first page whenever the tab or a filter changes.
  useEffect(() => {
    if (statusTab !== "open") loadHistory(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusTab, histFilters]);

  const currentUserId = localStorage.getItem("id");
  const currentUserRoles = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("roles") || "[]");
    } catch {
      return [];
    }
  }, []);
  const isAdmin = currentUserRoles.includes("admin") || currentUserRoles.includes("super_admin");

  // Trucks/dispatcher-name lookups don't need the same 60s refresh alerts
  // do (truck ownership rarely changes minute to minute) -- fetched once,
  // independent of the alerts hook's own polling.
  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const requests = [axios.get(`${apiBaseUrl}/trucks/?limit=1000`, authHeaders())];
    // Dispatcher names for the admin-only dispatcher grouping/chart --
    // skipped entirely for a dispatcher's own view, since the dispatcher
    // role has no ACL access to /users/ (confirmed: zero access_control_rules
    // rows for role=dispatcher/table=users) and a dispatcher never needs it
    // anyway, seeing only their own alerts.
    if (isAdmin) requests.push(axios.get(`${apiBaseUrl}/users/?limit=200`, authHeaders()));

    Promise.all(requests)
      .then(([trucksRes, usersRes]) => {
        setTrucks(trucksRes.data || []);
        if (usersRes) setUsers(usersRes.data || []);
      })
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  function refresh() {
    load();
    reloadAlerts();
  }

  // Marks an alert acknowledged (Triggered -> Acknowledged -> Resolved
  // lifecycle) -- doesn't hide it, just flips its acknowledged_at/by so the
  // card can grey it out. A silent alerts reload picks up the new state.
  function acknowledgeAlert(alertEventId) {
    if (!alertEventId) return;
    axios
      .post(`${apiBaseUrl}/scripted/fleet/acknowledge-alert`, { alert_event_id: alertEventId }, authHeaders())
      .then(() => reloadAlerts(true))
      .catch(() => {});
  }

  // Confirm/Dismiss a stop-detection candidate. Confirm routes the lifecycle
  // change through the canonical pickup/delivery business-rule path server-side
  // (so snapshots/economics fire exactly like a manual dispatcher confirmation);
  // Dismiss resolves the alert without touching the load. Either way the alert
  // is resolved and the feed reloads.
  function resolveMissedStop(alertEventId, action) {
    if (!alertEventId) return;
    axios
      .post(`${apiBaseUrl}/scripted/fleet/resolve-missed-stop`, { alert_event_id: alertEventId, action }, authHeaders())
      .then(() => reloadAlerts(true))
      .catch(() => {});
  }

  // Builds the "Opened from alert" strip content for RouteMonitorModal --
  // only toll_overage/off_route/eta_drift ever reach this (the only types
  // routed into the map below), each with its own real numbers already on
  // the alert object (same fields the card itself renders).
  function alertRouteContext(a) {
    if (a.type === "toll_overage") {
      return {
        leadText: "Toll overage",
        figs: [
          a.planned_toll_cost != null && a.actual_toll_total != null
            ? `Planned $${Number(a.planned_toll_cost).toFixed(2)} → Actual $${Number(a.actual_toll_total).toFixed(2)}`
            : null,
          a.toll_overage != null ? `$${Number(a.toll_overage).toFixed(2)} over plan` : null,
        ],
        cause: a.potential_cause === "route_deviation" ? "Potential cause: route deviation" : null,
      };
    }
    if (a.type === "off_route") {
      return {
        leadText: "Off route",
        figs: [
          a.distance_from_route_miles != null ? `${a.distance_from_route_miles}mi off planned route` : null,
          a.estimated_cost_impact != null ? `Estimated exposure $${Number(a.estimated_cost_impact).toFixed(2)}` : null,
        ],
        cause: null,
      };
    }
    if (a.type === "eta_drift") {
      const late = a.drift_hours > 0;
      return {
        leadText: "ETA drift",
        figs: [`${formatHm(Math.abs(a.drift_hours))} ${late ? "late" : "early"}`],
        cause: a.potential_cause === "route_deviation" ? "Potential cause: route deviation" : null,
      };
    }
    return null;
  }

  function openAlert(a) {
    const meta = TYPE_META[a.type] || {};
    // toll/off-route/eta-drift open the planned-vs-actual map directly,
    // with the alert's own numbers in a context strip -- there's real
    // route geometry to show for these, so navigating away to the
    // Operational Center would just make the dispatcher go find the same
    // thing themselves.
    if (meta.tab === "route" && a.load_id) {
      setRouteModal({ loadId: a.load_id, unitNumber: a.truck_unit_number, alertContext: alertRouteContext(a) });
      return;
    }
    if (!a.truck_id) return; // fleet-wide alert (e.g. motive_sync_stale) -- nothing to navigate to
    navigate(`/dispatch/operational-center?truck=${a.truck_id}&tab=${meta.tab || "overview"}`);
  }

  function openTruckOverview(truckId) {
    navigate(`/dispatch/operational-center?truck=${truckId}&tab=overview`);
  }

  const truckDispatcherById = useMemo(() => {
    const m = {};
    for (const t of trucks) {
      const ref = t.dispatcher_id;
      m[t.id] = (ref && typeof ref === "object" ? ref.id : ref) || null;
    }
    return m;
  }, [trucks]);
  const dispatcherNameById = useMemo(() => {
    const m = {};
    for (const u of users) {
      m[u.id] = `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email;
    }
    return m;
  }, [users]);

  const allAlerts = data?.alerts || [];
  // A dispatcher only ever sees alerts for trucks THEY own -- an alert on
  // a truck with no dispatcher assigned, or one owned by someone else,
  // never shows up for them (admin sees everything regardless). Fleet-wide
  // alerts (no truck_id at all, e.g. motive_sync_stale) are a real
  // exception to that rule on purpose -- a Motive outage affects every
  // truck regardless of who dispatches it, so it always passes through
  // instead of being (wrongly) filtered out because truckDispatcherById
  // has nothing to match an undefined truck_id against.
  const alerts = useMemo(
    () =>
      isAdmin
        ? allAlerts
        : allAlerts.filter((a) => !a.truck_id || truckDispatcherById[a.truck_id] === currentUserId),
    [allAlerts, isAdmin, truckDispatcherById, currentUserId]
  );

  const counts = useMemo(() => {
    const c = { critical: 0, warning: 0, info: 0 };
    for (const a of alerts) c[a.severity] = (c[a.severity] || 0) + 1;
    return c;
  }, [alerts]);

  const fleetWideAlerts = useMemo(() => alerts.filter((a) => !a.truck_id), [alerts]);

  // One card per truck, every alert that truck currently has rolled into
  // it, worst-truck-first -- this is the actual story: not "10 off-route
  // alerts" but "here are the trucks that need you, ranked."
  const truckGroups = useMemo(() => {
    const groups = {};
    for (const a of alerts) {
      if (!a.truck_id) continue;
      if (!groups[a.truck_id]) {
        groups[a.truck_id] = {
          truckId: a.truck_id,
          unitNumber: a.truck_unit_number,
          driverName: null,
          dispatcherId: truckDispatcherById[a.truck_id] || null,
          alerts: [],
        };
      }
      groups[a.truck_id].alerts.push(a);
      if (!groups[a.truck_id].driverName && a.driver_name) groups[a.truck_id].driverName = a.driver_name;
    }
    const list = Object.values(groups);
    for (const g of list) {
      g.worst = Math.min(...g.alerts.map((a) => SEVERITY_WEIGHT[a.severity] ?? 2));
    }
    list.sort((a, b) => a.worst - b.worst || b.alerts.length - a.alerts.length);
    return list;
  }, [alerts, truckDispatcherById]);

  // Admin-only second level: which dispatcher's trucks are hottest, same
  // dispatcher-then-truck rollup shape Truck Economics already uses, so
  // this page reads consistently with the rest of the app instead of
  // inventing its own grouping convention.
  const dispatcherGroups = useMemo(() => {
    if (!isAdmin) return [];
    const groups = {};
    for (const g of truckGroups) {
      const dId = g.dispatcherId || "unassigned";
      if (!groups[dId]) {
        groups[dId] = { dispatcherId: dId, name: dId === "unassigned" ? "Unassigned" : dispatcherNameById[dId] || "Unknown", trucks: [] };
      }
      groups[dId].trucks.push(g);
    }
    const list = Object.values(groups);
    for (const dg of list) {
      dg.worst = Math.min(...dg.trucks.map((t) => t.worst));
      dg.count = dg.trucks.reduce((sum, t) => sum + t.alerts.length, 0);
    }
    list.sort((a, b) => a.worst - b.worst || b.count - a.count);
    return list;
  }, [truckGroups, isAdmin, dispatcherNameById]);

  useEffect(() => {
    if (!autoExpandedRef.current && dispatcherGroups.length > 0) {
      const target = targetDispatcherId && dispatcherGroups.some((dg) => dg.dispatcherId === targetDispatcherId)
        ? targetDispatcherId
        : dispatcherGroups[0].dispatcherId;
      setExpandedDispatchers(new Set([target]));
      autoExpandedRef.current = true;
      if (target === targetDispatcherId) {
        // Scroll after the expanded group's rows have actually rendered,
        // not the collapsed header's position.
        requestAnimationFrame(() => targetGroupRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
      }
    }
  }, [dispatcherGroups, targetDispatcherId]);

  function toggleDispatcher(dispatcherId) {
    setExpandedDispatchers((prev) => {
      const next = new Set(prev);
      next.has(dispatcherId) ? next.delete(dispatcherId) : next.add(dispatcherId);
      return next;
    });
  }

  const byType = useMemo(() => {
    const groups = {};
    for (const a of alerts) {
      (groups[a.type] = groups[a.type] || []).push(a);
    }
    return groups;
  }, [alerts]);

  // The actual "group by type" view -- biggest category first, worst
  // severity first within each, so "let's check every toll overage" or
  // "who's off-route right now" is a single scan instead of opening every
  // truck card looking for that one alert type.
  const typeGroupsList = useMemo(
    () =>
      Object.keys(byType)
        .sort((a, b) => byType[b].length - byType[a].length)
        .map((type) => ({
          type,
          alerts: [...byType[type]].sort((a, b) => (SEVERITY_WEIGHT[a.severity] ?? 2) - (SEVERITY_WEIGHT[b.severity] ?? 2)),
        })),
    [byType]
  );

  const typeChartData = useMemo(
    () =>
      Object.keys(byType)
        .sort((a, b) => byType[b].length - byType[a].length)
        .map((type, i) => ({
          type,
          name: TYPE_META[type]?.label || type,
          count: byType[type].length,
          color: TYPE_CHART_COLORS[i % TYPE_CHART_COLORS.length],
        })),
    [byType]
  );

  const dispatcherChartData = useMemo(() => {
    if (!isAdmin) return [];
    return dispatcherGroups
      .map((dg) => ({ name: dg.name, count: dg.count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  }, [dispatcherGroups, isAdmin]);

  return (
    <div>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Alerts</h1>
          <div style={styles.subtitle}>
            {isAdmin
              ? "Which trucks need attention right now, and why -- across the whole fleet."
              : "Which of your trucks need attention right now, and why."}
          </div>
        </div>
        <button style={styles.refreshBtn} onClick={refresh} disabled={isLoading}>
          <RefreshCw size={13} style={isLoading ? { animation: "lv-spin 0.8s linear infinite" } : undefined} /> Refresh
        </button>
      </div>

      {/* Status tabs: OPEN = live feed (existing); RESOLVED/ALL = incident history */}
      <div style={styles.statusTabs}>
        {[["open", "Open"], ["resolved", "Resolved"], ["all", "All"]].map(([key, label]) => (
          <button
            key={key}
            style={{ ...styles.statusTab, ...(statusTab === key ? styles.statusTabActive : {}) }}
            onClick={() => setStatusTab(key)}
          >
            {label}
            {key === "open" && data?.total != null ? ` ${data.total}` : ""}
            {key !== "open" && statusTab === key && history.counts
              ? ` ${key === "all" ? history.counts.open_in_window + history.counts.resolved_in_window : history.counts.resolved_in_window}`
              : ""}
          </button>
        ))}
      </div>

      {combinedError && statusTab === "open" && <div style={styles.errorBox}>Couldn't load alerts: {combinedError}</div>}

      {isLoading && statusTab === "open" && <LoadingState label="Loading alerts…" />}

      {!isLoading && statusTab === "open" && (
      <div style={styles.summaryRow}>
        <SummaryCard severity="critical" count={counts.critical} />
        <SummaryCard severity="warning" count={counts.warning} />
        <SummaryCard severity="info" count={counts.info} />
        <div style={styles.summaryCard}>
          <div style={{ ...styles.summaryIcon, color: "var(--brand)", background: "rgba(29, 111, 216,0.08)" }}>
            <TruckIcon size={16} />
          </div>
          <div>
            <div style={styles.summaryValue}>{truckGroups.length}</div>
            <div style={styles.summaryLabel}>Trucks affected</div>
          </div>
        </div>
      </div>
      )}

      {!isLoading && statusTab === "open" && !combinedError && alerts.length === 0 && (
        <div style={styles.emptyState}>
          <AlertCircle size={22} color="var(--muted)" />
          <div style={styles.emptyTitle}>No alerts right now</div>
          <div style={styles.emptySubtitle}>
            {isAdmin ? "Every in-transit load is tracking to plan." : "None of your trucks need attention right now."}
          </div>
        </div>
      )}

      {!isLoading && statusTab === "open" && !combinedError && alerts.length > 0 && (
        <>
          {fleetWideAlerts.length > 0 && (
            <div style={styles.fleetWideBanner}>
              {fleetWideAlerts.map((a, i) => {
                const meta = TYPE_META[a.type] || {};
                const Icon = meta.icon || Info;
                const sev = SEVERITY_META[a.severity] || SEVERITY_META.info;
                return (
                  <div key={i} style={styles.fleetWideRow} title={a.message}>
                    <Icon size={16} style={{ color: sev.color, flexShrink: 0 }} />
                    <span style={{ ...styles.severityPill, color: sev.color, background: sev.bg }}>{sev.label}</span>
                    <span style={styles.fleetWideMessage}>{a.message}</span>
                  </div>
                );
              })}
            </div>
          )}

          <div style={styles.patternsLabel}>Fleet patterns</div>
          <div style={{ ...styles.chartsRow, gridTemplateColumns: isAdmin ? "1fr 1fr" : "1fr" }}>
            <div style={styles.chartCard}>
              <div style={styles.chartTitle}>By type</div>
              <ResponsiveContainer width="100%" height={140}>
                <BarChart data={typeChartData} layout="vertical" margin={{ left: 8, right: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10, fill: "var(--muted)" }} />
                  <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 10.5, fill: "var(--ink-2)" }} />
                  <RTooltip contentStyle={styles.tooltipBox} cursor={{ fill: "var(--bg)" }} />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                    {typeChartData.map((d) => (
                      <Cell key={d.type} fill={d.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {isAdmin && (
              <div style={styles.chartCard}>
                <div style={styles.chartTitle}>By dispatcher</div>
                <ResponsiveContainer width="100%" height={140}>
                  <BarChart data={dispatcherChartData} layout="vertical" margin={{ left: 8, right: 16 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" horizontal={false} />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10, fill: "var(--muted)" }} />
                    <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 10.5, fill: "var(--ink-2)" }} />
                    <RTooltip contentStyle={styles.tooltipBox} cursor={{ fill: "var(--bg)" }} />
                    <Bar dataKey="count" fill="#5b9bd5" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <div style={styles.groupByRow}>
            <div style={styles.patternsLabel}>
              {groupBy === "type"
                ? "Trucks needing attention, by type"
                : isAdmin
                ? "Trucks needing attention, by dispatcher"
                : "Your trucks needing attention"}
            </div>
            <div style={styles.groupByToggle}>
              <button
                style={{ ...styles.groupByBtn, ...(groupBy === "truck" ? styles.groupByBtnActive : {}) }}
                onClick={() => setGroupBy("truck")}
              >
                Truck
              </button>
              <button
                style={{ ...styles.groupByBtn, ...(groupBy === "type" ? styles.groupByBtnActive : {}) }}
                onClick={() => setGroupBy("type")}
              >
                Type
              </button>
            </div>
          </div>

          <div style={styles.truckList}>
            {groupBy === "type"
              ? typeGroupsList.map((g) => {
                  const meta = TYPE_META[g.type] || {};
                  const Icon = meta.icon || Info;
                  return (
                    <div key={g.type} style={styles.typeGroup}>
                      <div style={styles.typeGroupHead}>
                        <Icon size={14} style={{ color: "var(--ink-2)", flexShrink: 0 }} />
                        <span style={styles.typeGroupLabel}>{meta.label || g.type}</span>
                        <span style={styles.typeGroupCount}>{g.alerts.length}</span>
                      </div>
                      <div style={styles.typeGroupAlerts}>
                        {g.alerts.map((a, i) => {
                          const sev = SEVERITY_META[a.severity] || SEVERITY_META.info;
                          const metaParts = alertMetaParts(a);
                          return (
                            <div
                              key={i}
                              style={{ ...styles.alertRow, ...(a.acknowledged_at ? styles.alertRowAcked : {}) }}
                              title={a.message}
                            >
                              <div style={styles.alertRowMain} onClick={() => openAlert(a)}>
                                <span style={{ ...styles.severityDot, background: sev.color }} />
                                {a.truck_unit_number && <span style={styles.alertRowTruck}>Truck {a.truck_unit_number}</span>}
                                {a.driver_name && <span style={styles.truckCardDriver}>{a.driver_name}</span>}
                                {meta.stat && <span style={{ ...styles.alertRowStat, color: sev.color }}>{meta.stat(a)}</span>}
                                {a.destination && <span style={styles.alertRowDest}>&rarr; {a.destination}</span>}
                              </div>
                              {metaParts.length > 0 && <div style={styles.alertRowMeta}>{metaParts.join(" · ")}</div>}
                              <div style={styles.alertRowFooter}>
                                {MISSED_STOP_TYPES.has(a.type)
                                  ? <MissedStopActions alert={a} onResolve={resolveMissedStop} />
                                  : <AlertAckButton alert={a} onAcknowledge={acknowledgeAlert} />}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })
              : isAdmin
              ? dispatcherGroups.map((dg) => {
                  const expanded = expandedDispatchers.has(dg.dispatcherId);
                  const worstSeverity = Object.keys(SEVERITY_WEIGHT).find((k) => SEVERITY_WEIGHT[k] === dg.worst);
                  const worstMeta = SEVERITY_META[worstSeverity] || SEVERITY_META.info;
                  return (
                    <div
                      key={dg.dispatcherId}
                      style={styles.dispatcherGroup}
                      ref={dg.dispatcherId === targetDispatcherId ? targetGroupRef : undefined}
                    >
                      <div style={styles.dispatcherHead} onClick={() => toggleDispatcher(dg.dispatcherId)}>
                        <span style={{ ...styles.severityPill, color: worstMeta.color, background: worstMeta.bg }}>{worstMeta.label}</span>
                        <span style={styles.dispatcherName}>{dg.name}</span>
                        <span style={styles.dispatcherCount}>
                          {dg.count} alert{dg.count === 1 ? "" : "s"} · {dg.trucks.length} truck{dg.trucks.length === 1 ? "" : "s"}
                        </span>
                        <span style={{ marginLeft: "auto", color: "var(--muted)" }}>
                          {expanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                        </span>
                      </div>
                      {expanded && dg.trucks.map((t) => (
                        <TruckAlertCard key={t.truckId} truck={t} onOpenAlert={openAlert} onOpenTruck={openTruckOverview} onAcknowledge={acknowledgeAlert} onResolveMissedStop={resolveMissedStop} />
                      ))}
                    </div>
                  );
                })
              : truckGroups.map((t) => (
                  <TruckAlertCard key={t.truckId} truck={t} onOpenAlert={openAlert} onOpenTruck={openTruckOverview} onAcknowledge={acknowledgeAlert} onResolveMissedStop={resolveMissedStop} />
                ))}
          </div>
        </>
      )}

      {/* ---- Incident history (RESOLVED / ALL tabs) ---- */}
      {statusTab !== "open" && (() => {
        const fmtLocal = (iso) => (iso ? new Date(String(iso).endsWith("Z") ? iso : `${iso}Z`).toLocaleString("en-US", { month: "2-digit", day: "2-digit", hour: "numeric", minute: "2-digit" }) : "—");
        const setF = (k, v) => setHistFilters((f) => ({ ...f, [k]: v }));
        return (
          <div>
            <div style={styles.histFilterBar}>
              <select style={styles.histCtl} value={histFilters.severity} onChange={(e) => setF("severity", e.target.value)}>
                <option value="">All severities</option>
                <option value="critical">Critical</option><option value="warning">Warning</option><option value="info">Info</option>
              </select>
              <select style={styles.histCtl} value={histFilters.type} onChange={(e) => setF("type", e.target.value)}>
                <option value="">All types</option>
                {["possible_missed_delivery", "possible_missed_pickup", "pickup_overdue", "delivery_risk", "pickup_risk", "off_route", "toll_overage", "fuel_deviation", "eta_drift", "hos_violation_daily", "pto_conflict", "returning_driver_available", "motive_sync_stale", "samsara_sync_stale"].map((t) => (
                  <option key={t} value={t}>{typeLabel(t)}</option>
                ))}
              </select>
              {isAdmin && (
                <select style={styles.histCtl} value={histFilters.dispatcher} onChange={(e) => setF("dispatcher", e.target.value)}>
                  <option value="">All dispatchers</option>
                  {dispatcherGroups.filter((dg) => dg.dispatcherId !== "unassigned").map((dg) => (
                    <option key={dg.dispatcherId} value={dg.dispatcherId}>{dg.name}</option>
                  ))}
                </select>
              )}
              <input type="date" style={styles.histCtl} value={histFilters.from ? histFilters.from.slice(0, 10) : ""} onChange={(e) => setF("from", e.target.value || "")} title="From" />
              <input type="date" style={styles.histCtl} value={histFilters.to ? histFilters.to.slice(0, 10) : ""} onChange={(e) => setF("to", e.target.value ? `${e.target.value}T23:59:59` : "")} title="To" />
              <input style={{ ...styles.histCtl, minWidth: 150 }} placeholder="Truck / driver / load…" value={histFilters.q} onChange={(e) => setF("q", e.target.value)} />
              <button style={styles.histClear} onClick={() => setHistFilters({ severity: "", type: "", dispatcher: "", q: "", from: "", to: "" })}>Clear</button>
            </div>

            <div style={styles.histWindowLabel}>
              {windowLabel(statusTab, history.window)}
              {history.counts ? ` · ${history.counts.resolved_in_window} resolved · ${history.counts.open_in_window} open` : ""}
            </div>

            {history.error && <div style={styles.errorBox}>Couldn't load history: {history.error}</div>}
            {history.loading && history.incidents.length === 0 && <LoadingState label="Loading incidents…" />}
            {!history.loading && !history.error && history.incidents.length === 0 && (
              <div style={styles.emptyState}>
                <AlertCircle size={22} color="var(--muted)" />
                <div style={styles.emptyTitle}>No incidents match</div>
                <div style={styles.emptySubtitle}>Try a wider date range or fewer filters.</div>
              </div>
            )}

            <div style={styles.histList}>
              {history.incidents.map((inc) => {
                const sev = SEVERITY_META[inc.severity] || SEVERITY_META.info;
                const fact = keyFact(inc);
                const isResolved = !!inc.resolved_at;
                return (
                  <div key={inc.id} style={styles.histRow} onClick={() => setDetailIncident(inc)} title="View incident detail">
                    <span style={{ ...styles.severityDot, background: sev.color }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={styles.histRowTitle}>
                        {typeLabel(inc.alert_type)} · <span style={{ color: isResolved ? "var(--muted)" : sev.color }}>{isResolved ? "Resolved" : "Open"}</span>
                        {inc.truck_unit_number ? `  ·  Truck ${inc.truck_unit_number}` : ""}
                        {inc.driver_name ? ` · ${inc.driver_name}` : ""}
                      </div>
                      <div style={styles.histRowMeta}>
                        {fact ? `${fact} · ` : ""}Started {fmtLocal(inc.triggered_at)}
                        {isResolved ? ` · Resolved ${fmtLocal(inc.resolved_at)} · ${formatDuration(inc.duration_seconds)}` : " · ongoing"}
                        {inc.verified_cost_impact != null ? ` · $${Math.abs(inc.verified_cost_impact).toFixed(2)} ${inc.verified_cost_impact < 0 ? "under" : "over"} plan` : ""}
                        {inc.estimated_cost_impact != null ? ` · est. $${Math.abs(inc.estimated_cost_impact).toFixed(2)}` : ""}
                        {isAdmin && inc.dispatcher_name ? ` · ${inc.dispatcher_name}` : ""}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {history.page?.next_cursor && (
              <button style={styles.histLoadMore} onClick={() => loadHistory(false)} disabled={history.loading}>
                {history.loading ? "Loading…" : "Load more"}
              </button>
            )}
          </div>
        );
      })()}

      {detailIncident && (() => {
        const inc = detailIncident;
        const fmtLocal = (iso) => (iso ? new Date(String(iso).endsWith("Z") ? iso : `${iso}Z`).toLocaleString("en-US", { month: "2-digit", day: "2-digit", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—");
        const md = inc.metadata || {};
        const techKeys = Object.keys(md).filter((k) => !["confidence", "reason_codes"].includes(k));
        return (
          <div style={styles.drawerOverlay} onClick={() => setDetailIncident(null)}>
            <div style={styles.drawer} onClick={(e) => e.stopPropagation()}>
              <div style={styles.drawerHead}>
                <span style={styles.drawerBadge}>{typeLabel(inc.alert_type)}{inc.resolved_at ? " · Resolved" : " · Open"}</span>
                <span style={{ cursor: "pointer", color: "var(--muted)", fontWeight: 700 }} onClick={() => setDetailIncident(null)}>✕</span>
              </div>
              <div style={styles.drawerTitle}>{inc.truck_unit_number ? `Truck ${inc.truck_unit_number}` : "Fleet-wide"}{inc.driver_name ? ` · ${inc.driver_name}` : ""}</div>
              {keyFact(inc) && <p style={styles.drawerFact}>{keyFact(inc)}</p>}
              <div style={styles.drawerFacts}>
                <div style={styles.drawerRow}><span style={styles.drawerLbl}>Severity</span><span>{inc.severity}</span></div>
                <div style={styles.drawerRow}><span style={styles.drawerLbl}>Started</span><span>{fmtLocal(inc.triggered_at)}</span></div>
                {inc.resolved_at && <div style={styles.drawerRow}><span style={styles.drawerLbl}>Resolved</span><span>{fmtLocal(inc.resolved_at)}</span></div>}
                {inc.duration_seconds != null && <div style={styles.drawerRow}><span style={styles.drawerLbl}>Duration</span><span>{formatDuration(inc.duration_seconds)}</span></div>}
                {inc.acknowledged_at && <div style={styles.drawerRow}><span style={styles.drawerLbl}>Acknowledged</span><span>{fmtLocal(inc.acknowledged_at)}</span></div>}
                {inc.resolution && <div style={styles.drawerRow}><span style={styles.drawerLbl}>Resolution</span><span>{inc.resolution.replace(/_/g, " ")}</span></div>}
                {inc.planned_value != null && <div style={styles.drawerRow}><span style={styles.drawerLbl}>Planned</span><span>{inc.planned_value}</span></div>}
                {inc.actual_value != null && <div style={styles.drawerRow}><span style={styles.drawerLbl}>Actual</span><span>{inc.actual_value}</span></div>}
                {inc.verified_cost_impact != null && <div style={styles.drawerRow}><span style={styles.drawerLbl}>Verified impact</span><span>${inc.verified_cost_impact.toFixed(2)}</span></div>}
                {inc.estimated_cost_impact != null && <div style={styles.drawerRow}><span style={styles.drawerLbl}>Est. exposure</span><span>${inc.estimated_cost_impact.toFixed(2)}</span></div>}
                {isAdmin && inc.dispatcher_name && <div style={styles.drawerRow}><span style={styles.drawerLbl}>Dispatcher</span><span>{inc.dispatcher_name}</span></div>}
              </div>
              {isRouteType(inc.alert_type) && inc.load_id && (
                <button style={styles.drawerRouteBtn} onClick={() => { setRouteModal({ loadId: inc.load_id, unitNumber: inc.truck_unit_number, alertContext: null }); setDetailIncident(null); }}>Review route on map</button>
              )}
              {techKeys.length > 0 && (
                <details style={{ marginTop: 10, fontSize: 12 }}>
                  <summary style={{ cursor: "pointer", color: "var(--muted)" }}>Technical details</summary>
                  <pre style={styles.drawerTech}>{JSON.stringify(md, null, 2)}</pre>
                </details>
              )}
            </div>
          </div>
        );
      })()}

      {data?.generated_at && statusTab === "open" && (
        <div style={styles.footer}>Computed {new Date(data.generated_at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}</div>
      )}

      {routeModal && (
        <RouteMonitorModal
          loadId={routeModal.loadId}
          unitNumber={routeModal.unitNumber}
          alertContext={routeModal.alertContext}
          onClose={() => setRouteModal(null)}
        />
      )}
    </div>
  );
}

// The extra context line the richer card design calls for: a real
// Planned/Actual pair when one exists (toll_overage; eta_drift's original
// vs live ETA), the dispatcher who owns the truck, and a cheap
// cross-referenced "potential cause" (see backend: an open off_route event
// on the same load) -- turns "Toll overage +$37" into something a
// dispatcher can act on without opening the truck.
function alertMetaParts(a) {
  const parts = [];
  if (a.type === "toll_overage" && a.planned_toll_cost != null && a.actual_toll_total != null) {
    parts.push(`Planned $${Number(a.planned_toll_cost).toFixed(2)} · Actual $${Number(a.actual_toll_total).toFixed(2)}`);
  }
  if (a.type === "eta_drift" && a.original_eta && a.live_eta) {
    parts.push(`Planned ${new Date(a.original_eta).toLocaleString("en-US", { month: "2-digit", day: "2-digit", year: "numeric", hour: "numeric", minute: "2-digit" })} · Live ${new Date(a.live_eta).toLocaleString("en-US", { month: "2-digit", day: "2-digit", year: "numeric", hour: "numeric", minute: "2-digit" })}`);
  }
  if (a.estimated_cost_impact != null) parts.push(`Estimated exposure $${Number(a.estimated_cost_impact).toFixed(2)}`);
  if (a.verified_cost_impact != null) {
    const v = Number(a.verified_cost_impact);
    parts.push(v < 0 ? `$${Math.abs(v).toFixed(2)} under plan` : `$${v.toFixed(2)} over plan`);
  }
  if (MISSED_STOP_TYPES.has(a.type)) {
    if (a.dwell_minutes != null) parts.push(`Dwelled ${formatHm(a.dwell_minutes / 60)}`);
    if (a.closest_distance_miles != null) parts.push(`Closest ${Number(a.closest_distance_miles).toFixed(1)}mi`);
    if (a.appointment_at) parts.push(`Appt ${new Date(a.appointment_at).toLocaleString("en-US", { month: "2-digit", day: "2-digit", hour: "numeric", minute: "2-digit" })}`);
    if (Array.isArray(a.reason_codes) && a.reason_codes.length) parts.push(a.reason_codes.join(", "));
    if (a.source) parts.push(a.source === "recovery" ? "Detected from trip history" : "Detected live");
  }
  if (a.dispatcher_name) parts.push(`Dispatcher: ${a.dispatcher_name}`);
  if (a.potential_cause === "route_deviation") parts.push("Potential cause: route deviation");
  return parts;
}

// Confirm/Dismiss for a stop-detection candidate. Confirm carries the leg so
// the server routes pickup vs delivery through the correct canonical path.
function MissedStopActions({ alert, onResolve }) {
  if (alert.resolved_at) return null;
  const isPickup = alert.type === "possible_missed_pickup";
  return (
    <div style={{ display: "flex", gap: 6 }}>
      <button
        style={styles.ackBtn}
        onClick={(e) => { e.stopPropagation(); onResolve(alert.alert_event_id, "confirm"); }}
        title={`Confirm ${isPickup ? "pickup" : "delivery"} for this load`}
      >
        <Check size={11} /> Confirm {isPickup ? "pickup" : "delivery"}
      </button>
      <button
        style={styles.ackBtn}
        onClick={(e) => { e.stopPropagation(); onResolve(alert.alert_event_id, "dismiss"); }}
        title="Dismiss -- this was not a missed stop"
      >
        Dismiss
      </button>
    </div>
  );
}

function AlertAckButton({ alert, onAcknowledge }) {
  if (alert.acknowledged_at) {
    return (
      <span style={styles.ackedPill} title={`Acknowledged ${new Date(alert.acknowledged_at).toLocaleString("en-US", { month: "2-digit", day: "2-digit", year: "numeric", hour: "numeric", minute: "2-digit" })}`}>
        <Check size={11} /> Seen
      </span>
    );
  }
  return (
    <button
      style={styles.ackBtn}
      onClick={(e) => {
        e.stopPropagation();
        onAcknowledge(alert.alert_event_id);
      }}
      title="Acknowledge this alert"
    >
      Acknowledge
    </button>
  );
}

function SummaryCard({ severity, count }) {
  const sev = SEVERITY_META[severity];
  const Icon = sev.icon;
  return (
    <div style={styles.summaryCard}>
      <div style={{ ...styles.summaryIcon, color: sev.color, background: sev.bg }}>
        <Icon size={16} />
      </div>
      <div>
        <div style={styles.summaryValue}>{count}</div>
        <div style={styles.summaryLabel}>{sev.label}</div>
      </div>
    </div>
  );
}

function TruckAlertCard({ truck, onOpenAlert, onOpenTruck, onAcknowledge, onResolveMissedStop }) {
  const worstMeta = SEVERITY_META[Object.keys(SEVERITY_WEIGHT).find((k) => SEVERITY_WEIGHT[k] === truck.worst)] || SEVERITY_META.info;
  return (
    <div style={{ ...styles.truckCard, borderLeft: `4px solid ${worstMeta.color}` }}>
      <div style={styles.truckCardHead} onClick={() => onOpenTruck(truck.truckId)}>
        <TruckIcon size={14} style={{ color: "var(--muted)" }} />
        <span style={styles.truckCardUnit}>Truck {truck.unitNumber}</span>
        {truck.driverName && <span style={styles.truckCardDriver}>{truck.driverName}</span>}
        <span style={{ ...styles.severityPill, color: worstMeta.color, background: worstMeta.bg, marginLeft: "auto" }}>
          {worstMeta.label}
        </span>
      </div>
      <div style={styles.truckCardAlerts}>
        {truck.alerts.map((a, i) => {
          const meta = TYPE_META[a.type] || {};
          const Icon = meta.icon || Info;
          const sev = SEVERITY_META[a.severity] || SEVERITY_META.info;
          const metaParts = alertMetaParts(a);
          return (
            <div
              key={i}
              style={{ ...styles.alertRow, ...(a.acknowledged_at ? styles.alertRowAcked : {}) }}
              title={a.message}
            >
              <div style={styles.alertRowMain} onClick={() => onOpenAlert(a)}>
                <Icon size={13} style={{ color: sev.color, flexShrink: 0 }} />
                <span style={styles.alertRowLabel}>{meta.label || a.type}</span>
                {meta.stat && <span style={{ ...styles.alertRowStat, color: sev.color }}>{meta.stat(a)}</span>}
                {a.destination && <span style={styles.alertRowDest}>&rarr; {a.destination}</span>}
                {meta.tab === "route" && (
                  <span style={styles.viewRouteLink}>
                    View route <ArrowRight size={11} />
                  </span>
                )}
              </div>
              {metaParts.length > 0 && <div style={styles.alertRowMeta}>{metaParts.join(" · ")}</div>}
              <div style={styles.alertRowFooter}>
                {MISSED_STOP_TYPES.has(a.type)
                  ? <MissedStopActions alert={a} onResolve={onResolveMissedStop} />
                  : <AlertAckButton alert={a} onAcknowledge={onAcknowledge} />}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const styles = {
  header: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20 },
  title: { fontSize: 22, fontWeight: 800, color: "var(--ink)", margin: 0 },
  subtitle: { fontSize: 13, color: "var(--muted)", marginTop: 4, maxWidth: 560 },
  refreshBtn: {
    display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 600, color: "var(--ink-2)",
    background: "var(--card)", border: "1px solid var(--line)", borderRadius: 8, padding: "7px 14px", cursor: "pointer",
  },

  summaryRow: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 20 },
  summaryCard: {
    display: "flex", alignItems: "center", gap: 12, background: "var(--card)", border: "1px solid var(--line)",
    borderRadius: "var(--radius-lg)", padding: "16px 18px", boxShadow: "var(--shadow-xs)",
  },
  summaryIcon: {
    width: 34, height: 34, borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  summaryValue: { fontSize: 20, fontWeight: 800, color: "var(--ink)", lineHeight: 1.2 },
  summaryLabel: { fontSize: 12, color: "var(--muted)", marginTop: 2 },

  errorBox: {
    background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.2)", color: "#dc2626",
    borderRadius: "var(--radius-md)", padding: "12px 16px", fontSize: 13, marginBottom: 16,
  },
  emptyState: {
    display: "flex", flexDirection: "column", alignItems: "center", gap: 6, textAlign: "center",
    background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)", padding: "40px 20px",
  },
  emptyTitle: { fontSize: 14, fontWeight: 700, color: "var(--ink)" },
  emptySubtitle: { fontSize: 12.5, color: "var(--muted)" },

  fleetWideBanner: {
    display: "flex", flexDirection: "column", gap: 8, marginBottom: 20,
  },
  fleetWideRow: {
    display: "flex", alignItems: "center", gap: 10, background: "rgba(220,38,38,0.05)",
    border: "1px solid rgba(220,38,38,0.25)", borderRadius: "var(--radius-lg)", padding: "12px 16px",
  },
  fleetWideMessage: { fontSize: 13, color: "var(--ink)", fontWeight: 600 },

  patternsLabel: {
    fontSize: 11.5, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8,
  },
  groupByRow: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 },
  groupByToggle: {
    display: "flex", background: "var(--card)", border: "1px solid var(--line)", borderRadius: 8, padding: 2, gap: 2,
  },
  groupByBtn: {
    fontSize: 12, fontWeight: 600, color: "var(--muted)", background: "transparent", border: "none",
    borderRadius: 6, padding: "5px 12px", cursor: "pointer",
  },
  groupByBtnActive: { background: "var(--brand)", color: "#fff" },

  typeGroup: {
    background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)",
    padding: "12px 14px", boxShadow: "var(--shadow-xs)",
  },
  typeGroupHead: { display: "flex", alignItems: "center", gap: 8, marginBottom: 8 },
  typeGroupLabel: { fontSize: 13.5, fontWeight: 800, color: "var(--ink)" },
  typeGroupCount: {
    fontSize: 11, fontWeight: 700, color: "var(--muted)", background: "var(--bg)",
    borderRadius: 999, padding: "1px 8px",
  },
  typeGroupAlerts: { display: "flex", flexDirection: "column", gap: 4 },
  severityDot: { width: 7, height: 7, borderRadius: "50%", flexShrink: 0 },
  alertRowTruck: { fontSize: 12.5, fontWeight: 700, color: "var(--ink)" },
  chartsRow: { display: "grid", gap: 14, marginBottom: 20 },
  chartCard: {
    background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)",
    padding: "14px 16px", boxShadow: "var(--shadow-xs)",
  },
  chartTitle: { fontSize: 11.5, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 },
  tooltipBox: { background: "var(--card)", border: "1px solid var(--line)", borderRadius: 8, fontSize: 12 },

  truckList: { display: "flex", flexDirection: "column", gap: 10 },
  dispatcherGroup: { display: "flex", flexDirection: "column", gap: 10 },
  dispatcherHead: {
    display: "flex", alignItems: "center", gap: 10, cursor: "pointer", background: "var(--card)",
    border: "1px solid var(--line)", borderRadius: "var(--radius-lg)", padding: "12px 14px", boxShadow: "var(--shadow-xs)",
  },
  dispatcherName: { fontSize: 14, fontWeight: 800, color: "var(--ink)" },
  dispatcherCount: { fontSize: 12, color: "var(--muted)" },

  truckCard: {
    background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)",
    padding: "12px 14px", boxShadow: "var(--shadow-xs)",
  },
  truckCardHead: { display: "flex", alignItems: "center", gap: 8, cursor: "pointer", marginBottom: 8 },
  truckCardUnit: { fontSize: 13.5, fontWeight: 800, color: "var(--ink)" },
  truckCardDriver: { fontSize: 12.5, color: "var(--muted)" },
  severityPill: { fontSize: 10.5, fontWeight: 700, padding: "2px 8px", borderRadius: 999, flexShrink: 0 },

  truckCardAlerts: { display: "flex", flexDirection: "column", gap: 4 },
  alertRow: {
    display: "flex", flexDirection: "column", gap: 5, background: "var(--bg)", border: "1px solid var(--line)",
    borderRadius: "var(--radius-md)", padding: "7px 12px 8px",
  },
  alertRowAcked: { opacity: 0.6 },
  alertRowMain: { display: "flex", alignItems: "center", gap: 8, cursor: "pointer" },
  alertRowLabel: { fontSize: 12.5, fontWeight: 600, color: "var(--ink-2)" },
  alertRowStat: { fontSize: 12.5, fontWeight: 800 },
  alertRowDest: { fontSize: 12, color: "var(--muted)", marginLeft: "auto", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  viewRouteLink: {
    fontSize: 11.5, fontWeight: 700, color: "var(--brand)", display: "flex", alignItems: "center", gap: 3, flexShrink: 0,
  },
  alertRowMeta: { fontSize: 11.5, color: "var(--muted)", paddingLeft: 21 },
  alertRowFooter: { display: "flex", justifyContent: "flex-end" },
  ackBtn: {
    fontSize: 11, fontWeight: 700, color: "var(--brand)", background: "transparent",
    border: "1px solid var(--line)", borderRadius: 6, padding: "3px 10px", cursor: "pointer",
  },
  ackedPill: {
    display: "flex", alignItems: "center", gap: 3, fontSize: 11, fontWeight: 700, color: "var(--muted)",
    background: "var(--card)", border: "1px solid var(--line)", borderRadius: 999, padding: "2px 9px",
  },

  footer: { fontSize: 11.5, color: "var(--muted)", marginTop: 14, textAlign: "right" },

  // --- incident history (Phase 1) ---
  statusTabs: { display: "flex", gap: 4, margin: "4px 0 16px", borderBottom: "1px solid var(--line)" },
  statusTab: { fontSize: 13, fontWeight: 700, color: "var(--muted)", background: "none", border: "none", borderBottom: "2px solid transparent", padding: "8px 14px", cursor: "pointer", marginBottom: -1 },
  statusTabActive: { color: "var(--ink)", borderBottomColor: "var(--brand)" },
  histFilterBar: { display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 10 },
  histCtl: { fontSize: 12.5, color: "var(--ink)", background: "var(--card)", border: "1px solid var(--line)", borderRadius: 8, padding: "6px 10px" },
  histClear: { fontSize: 12.5, fontWeight: 600, color: "var(--ink-2)", background: "none", border: "1px solid var(--line)", borderRadius: 8, padding: "6px 12px", cursor: "pointer" },
  histWindowLabel: { fontSize: 12, color: "var(--muted)", marginBottom: 10 },
  histList: { display: "flex", flexDirection: "column", gap: 6 },
  histRow: { display: "flex", gap: 10, alignItems: "flex-start", padding: "10px 12px", background: "var(--card)", border: "1px solid var(--line)", borderRadius: 10, cursor: "pointer" },
  histRowTitle: { fontSize: 13, fontWeight: 700, color: "var(--ink)" },
  histRowMeta: { fontSize: 12, color: "var(--muted)", marginTop: 3, lineHeight: 1.4 },
  histLoadMore: { marginTop: 12, fontSize: 13, fontWeight: 700, color: "var(--ink-2)", background: "var(--card)", border: "1px solid var(--line)", borderRadius: 8, padding: "9px 18px", cursor: "pointer" },
  drawerOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.28)", zIndex: 2400, display: "flex", justifyContent: "flex-end" },
  drawer: { width: 440, maxWidth: "92vw", height: "100%", background: "var(--card)", borderLeft: "1px solid var(--line)", boxShadow: "-8px 0 24px rgba(0,0,0,0.18)", padding: 20, overflowY: "auto", display: "flex", flexDirection: "column", gap: 12 },
  drawerHead: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  drawerBadge: { fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".04em", color: "var(--ink-2)", background: "var(--bg)", border: "1px solid var(--line)", borderRadius: 6, padding: "3px 8px" },
  drawerTitle: { fontSize: 18, fontWeight: 800, color: "var(--ink)" },
  drawerFact: { fontSize: 13.5, color: "var(--ink-2)", lineHeight: 1.5, margin: 0 },
  drawerFacts: { display: "flex", flexDirection: "column", gap: 6, borderTop: "1px solid var(--line)", borderBottom: "1px solid var(--line)", padding: "10px 0" },
  drawerRow: { display: "flex", justifyContent: "space-between", fontSize: 12.5, color: "var(--ink)" },
  drawerLbl: { color: "var(--muted)" },
  drawerRouteBtn: { fontSize: 13, fontWeight: 700, color: "#fff", background: "var(--brand)", border: "none", borderRadius: 8, padding: "9px 14px", cursor: "pointer" },
  drawerTech: { marginTop: 6, fontSize: 11, background: "var(--bg)", border: "1px solid var(--line)", borderRadius: 8, padding: 10, overflowX: "auto", whiteSpace: "pre-wrap", color: "var(--ink-2)" },
};
